import type { Icon } from '@phosphor-icons/react';
import { OUTREACH_STATES } from '@discovr/types';
import type { OutreachCounts, OutreachInboxTab, OutreachState, OutreachTab } from '@discovr/types';
import type { OutreachRowsParams } from '@discovr/features';
import { Clock, Envelope, FileText, Warning } from '../../../design-system/icons';

export const INBOX_TABS: ReadonlyArray<{ key: OutreachInboxTab; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'not_contacted', label: 'Not contacted' },
  { key: 'waiting_on_brand', label: 'With brand' },
  { key: 'stopped', label: 'Stopped' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'removed', label: 'Removed' },
  { key: 'needs_you', label: 'Needs you' },
];

export const INBOX_TAB_TO_API: Record<OutreachInboxTab, OutreachTab> = {
  all: 'all',
  contacted: 'in_conversation',
  not_contacted: 'sourced',
  waiting_on_brand: 'with_brand',
  stopped: 'stopped',
  scheduled: 'scheduled',
  removed: 'removed',
  needs_you: 'needs_you',
};

/** Pre-collapse tab keys still in shared links; nothing else validates `pill`, so an unknown key falls back to All. */
const LEGACY_INBOX_TABS: Readonly<Record<string, OutreachInboxTab>> = {
  rejected: 'stopped',
  ghosted: 'stopped',
  closed: 'stopped',
  rate_provided: 'waiting_on_brand',
  // The With brand tab's API key, used in hand-written links.
  with_brand: 'waiting_on_brand',
};

export function parseInboxTab(raw: string): OutreachInboxTab {
  if (INBOX_TABS.some((tab) => tab.key === raw)) return raw as OutreachInboxTab;
  return (Object.hasOwn(LEGACY_INBOX_TABS, raw) ? LEGACY_INBOX_TABS[raw] : undefined) ?? 'all';
}

export const INBOX_SORTS: ReadonlyArray<{ value: NonNullable<OutreachRowsParams['sort']>; label: string }> = [
  { value: 'last_activity', label: 'Last activity' },
  { value: 'name', label: 'Name A–Z' },
  { value: 'status', label: 'Stage' },
  { value: 'scheduled_at', label: 'Scheduled time' },
];

/** Tabs are derived, never stored (spec §2.1c). Each state sits in exactly one state tab; keyed by state so a new
 *  OutreachState fails to compile here until it is placed. `all`, `scheduled`, `needs_you` and the brand side are counted apart. */
type StateTab = Exclude<OutreachInboxTab, 'all' | 'scheduled' | 'needs_you'>;
const STATE_TAB: Record<OutreachState, StateTab> = {
  sourced: 'not_contacted',
  contacted: 'contacted',
  replied: 'contacted',
  rate_provided: 'waiting_on_brand',
  // Q-H: every outreach close reason is Stopped; Removed is removed plus the dashboard rejections.
  rejected: 'stopped',
  not_interested: 'stopped',
  ghosted: 'stopped',
  bounced: 'stopped',
  unsubscribed: 'stopped',
  removed: 'removed',
};
const statesIn = (tab: StateTab): readonly OutreachState[] => OUTREACH_STATES.filter((state) => STATE_TAB[state] === tab);

/**
 * The `stage` a tab actually sends. Stopped narrows the stage filter to its own states (never Removed's); `null` means
 * nothing is left, and the caller must skip the request: Go reads an empty state list as "no state filter" and would
 * return every outreach row instead of none.
 */
export function inboxTabStage(tab: OutreachInboxTab, stage: string | undefined): string | undefined | null {
  if (tab !== 'stopped') return stage;
  const stopped = statesIn('stopped');
  if (!stage) return stopped.join(',');
  const picked = new Set(stage.split(','));
  const narrowed = stopped.filter((state) => picked.has(state));
  return narrowed.length > 0 ? narrowed.join(',') : null;
}

// The wire mapper zero-fills every state; this guards counts built anywhere else (a cache write, a test double) from NaN.
const sumStates = (counts: OutreachCounts, states: readonly OutreachState[]) =>
  states.reduce((total, state) => total + ((counts.byState as Partial<Record<OutreachState, number>>)[state] ?? 0), 0);
const inConversation = (counts: OutreachCounts) => sumStates(counts, statesIn('contacted'));
const withTheBrand = (counts: OutreachCounts) => counts.withBrand + sumStates(counts, statesIn('waiting_on_brand'));
const removedOrRejected = (counts: OutreachCounts) => counts.removedOrRejected + sumStates(counts, statesIn('removed'));
const notRemoved = OUTREACH_STATES.filter((state) => STATE_TAB[state] !== 'removed');

export function inboxTabCount(counts: OutreachCounts | undefined, tab: OutreachInboxTab): number | undefined {
  if (!counts) return undefined;
  switch (tab) {
    // All is every creator in the campaign's outreach but Removed: the state tabs, Stopped, and the handed-off rows
    // (owner R17-48, 2026-10-27; Go's allTabClause owns the definition).
    case 'all': return sumStates(counts, notRemoved) + counts.withBrand;
    case 'waiting_on_brand': return withTheBrand(counts);
    case 'removed': return removedOrRejected(counts);
    case 'scheduled': return counts.scheduled;
    // An overlay: a row stays in its state tab too, so this count is not part of the partition. An older Go omits it.
    case 'needs_you': return counts.needsYou ?? 0;
    // Go #711 counts first-touch attempts into these two; an older Go leaves them out and the state sums stand.
    case 'contacted': return counts.inConversation ?? sumStates(counts, statesIn(tab));
    case 'not_contacted': return counts.notContacted ?? sumStates(counts, statesIn(tab));
    case 'stopped': return sumStates(counts, statesIn(tab));
    default: return assertNever(tab);
  }
}

function assertNever(tab: never): never {
  throw new Error(`Unhandled outreach inbox tab: ${String(tab)}`);
}

export function inboxSubline(counts: OutreachCounts): string {
  return `${counts.notContacted ?? sumStates(counts, ['sourced'])} still to reach out to · ${inConversation(counts)} in conversation · ${withTheBrand(counts)} with the brand`;
}

const NO_CREATORS = { title: 'No creators yet', body: 'Pull a shortlist from Discovery or upload a spreadsheet to start.' };

export const INBOX_EMPTY_COPY: Record<OutreachInboxTab, { title: string; body: string }> = {
  all: NO_CREATORS,
  contacted: NO_CREATORS,
  not_contacted: NO_CREATORS,
  waiting_on_brand: { title: 'Nothing with the brand yet', body: 'Lock a rate in a thread, then move it to the brand dashboard.' },
  stopped: {
    title: 'Nobody stopped',
    body: 'Creators who said no, went quiet, bounced or unsubscribed appear here. Bounced and unsubscribed threads open read-only.',
  },
  scheduled: { title: 'Nothing scheduled', body: 'Sends you schedule for later appear here until they fire. You can edit the time or cancel any of them.' },
  // Removed holds removed creators and the dashboard rejections; an outreach close as rejected is Stopped.
  removed: { title: 'Nothing removed or rejected', body: 'Removed and rejected creators appear here.' },
  needs_you: { title: 'Nothing needs you', body: 'Creators who need a reply or a decision from you appear here, and stay in their own tab too.' },
};

/** Per-tab empty-state icon (Figma): mail for the three "no creators" tabs,
 *  a document for the stopped/parked tabs, a clock for Scheduled. */
export const INBOX_EMPTY_ICON: Record<OutreachInboxTab, Icon> = {
  all: Envelope,
  contacted: Envelope,
  not_contacted: Envelope,
  waiting_on_brand: FileText,
  stopped: FileText,
  scheduled: Clock,
  removed: FileText,
  needs_you: Warning,
};

/** The Outreach tab's nav badge (ruling 50) — `counts.unread`, replacing the
 *  old `counts.replied`. Hidden (undefined) at zero so the badge disappears
 *  rather than showing "0". */
export function outreachTabBadge(counts?: OutreachCounts): number | undefined {
  return counts && counts.unread > 0 ? counts.unread : undefined;
}
